import './EditTester.scss';
import { useEffect, useState, useContext } from 'react';
import { reactPlugin } from '../../../../services/appInsights.service';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { StateLookUp, Tester, TesterValidationSchema, validateTesterForm } from '../../../../interfaces/tester.interface';
import { Button, Form, Col, Row } from 'react-bootstrap';
import { TesterService } from '../../../../services/tester.service';
import { ConfirmationModelPopup, ComponentModelPopup } from '../../../../interfaces/modal.interface';
import ConfirmationModel from '../../appUtilityComponent/ConfirmationModel/ConfirmationModel';
import ErrorComponentModel from '../../appUtilityComponent/ErrorComponentModel/ErrorComponentModel';
import { Formik } from 'formik';
import DatePicker from "../../../../helpers/DatePicker";
import { getFormattedShortDate } from '../../../../helpers/Utilities';
import Spinner from '../../../components/Spinner';
import { UserAuthContext, Authorize } from '../../appLoginComponent/UserAuthContext'

type TParams = { readonly testerId: number, readonly onCancel: any, readonly onSuccess: any };

interface FormFieldProps {
  name: string;
  label: string;
  value: string | Date;
  touched?: boolean;
  error?: string;
  onChange: (e: any) => void;
  onBlur: (e: any) => void;
}

interface SelectFieldProps extends FormFieldProps {
  options: StateLookUp[];
  required?: boolean;
}

interface TextAreaFieldProps extends FormFieldProps {
  rows?: number;
  maxLength?: number;
}

const FormTextField = ({ name, label, value, touched, error, onChange, onBlur }: FormFieldProps) => (
  <Form.Group className={"mb-1 form-floating" + ((value && String(value).length > 0) ? "filled" : "")}>
    <Form.Control
      autoComplete="off"
      type="text"
      name={name}
      onChange={onChange}
      onBlur={onBlur}
      value={String(value)}
      className={touched && error ? "has-error" : ""}
    />
    <Form.Label className="mb-0">{label}</Form.Label>
    {touched && error && <div className="text-danger font-small">{error}</div>}
  </Form.Group>
);

const FormSelectField = ({ name, label, value, touched, error, onChange, onBlur, options, required }: SelectFieldProps) => (
  <Form.Group className={"mb-1 form-floating" + (required ? " required " : " ") + ((value && String(value).length > 0) ? "filled" : "")}>
    <Form.Control
      autoComplete="off"
      as="select"
      name={name}
      onChange={onChange}
      onBlur={onBlur}
      value={String(value)}
      className={touched && error ? "has-error" : ""}
    >
      <option value=""></option>
      {options.map((state: StateLookUp) => (
        <option key={state.StateCode} value={state.StateCode}>{state.StateCode}</option>
      ))}
    </Form.Control>
    <Form.Label className="mb-0">{label}</Form.Label>
    {touched && error && <div className="text-danger font-small">{error}</div>}
  </Form.Group>
);

const FormTextAreaField = ({ name, label, value, touched, error, onChange, onBlur, maxLength, rows = 3 }: TextAreaFieldProps) => (
  <Form.Group className={"mb-1 form-floating " + ((value && String(value).length > 0) ? "filled" : "")}>
    <Form.Control
      autoComplete="off"
      as="textarea"
      rows={rows}
      name={name}
      maxLength={maxLength}
      onChange={onChange}
      onBlur={onBlur}
      value={String(value)}
      className={touched && error ? "has-error" : ""}
    />
    <Form.Label className="mb-0">{label}</Form.Label>
    {touched && error && <div className="text-danger font-small">{error}</div>}
  </Form.Group>
);

const isFieldFilled = (value: any): boolean => value && String(value).length > 0;

const hasMailingAddressData = (values: Tester): boolean => {
  return !!(
    (values.mailingAddrLine2 && values.mailingAddrLine2.length > 0) ||
    (values.mailingAddrLine1 && values.mailingAddrLine1.length > 0) ||
    (values.mailingAddrstateCD && values.mailingAddrstateCD.length > 0) ||
    (values.mailingAddrZip && values.mailingAddrZip.length > 0)
  );
};

const getMailingAddressRequiredClass = (isLine1: boolean, values: Tester): string => {
  return hasMailingAddressData(values) ? " required " : "";
};

interface FormSectionProps {
  values: Tester;
  errors: any;
  touched: any;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  states?: StateLookUp[];
  isAuthorized?: (role: string) => boolean;
}

const getFilledClass = (value: string): string => ((value && value.length > 0) ? "filled" : "");

const getErrorClass = (touched: boolean, error: string): string => (touched && error ? "has-error" : "");

const getMailingAddressFieldClass = (fieldValue: string): string => {
  return (fieldValue && fieldValue.length > 0) ? "filled" : "";
};

const getMailingAddressRequiredFieldClass = (values: Tester): string => {
  return hasMailingAddressData(values) ? " required " : "";
};

const isMailingAddressPartiallyFilled = (values: Tester): boolean => {
  return !!(
    (values.mailingAddrLine2 && values.mailingAddrLine2.length > 0) ||
    (values.mailingAddrLine1 && values.mailingAddrLine1.length > 0) ||
    (values.mailingAddrstateCD && values.mailingAddrstateCD.length > 0) ||
    (values.mailingAddrCity && values.mailingAddrCity.length > 0) ||
    (values.mailingAddrZip && values.mailingAddrZip.length > 0)
  );
};

const getMailingFieldClass = (fieldValue: string, hasMailingData: boolean): string => {
  return "mb-1 form-floating " +
    ((fieldValue && fieldValue.length > 0) ? "filled " : "") +
    (hasMailingData ? "required" : "");
};

const getContactFieldClass = (fieldValue: string, isRequired: boolean = true): string => {
  return "mb-1 form-floating " +
    ((fieldValue && fieldValue.length > 0) ? "filled " : "") +
    (isRequired ? "required" : "");
};

const formatTesterData = (tester: Tester): Tester => {
  return {
    ...tester,
    testerEffectiveDate: new Date(tester.testerEffectiveDate),
    mailingAddrstateCD: tester.mailingAddrstateCD?.trim() || "",
    physicalAddrLine2: tester.physicalAddrLine2 || "",
    mailingAddrLine1: tester.mailingAddrLine1 || "",
    mailingAddrLine2: tester.mailingAddrLine2 || "",
    mailingAddrCity: tester.mailingAddrCity || "",
    mailingAddrZip: tester.mailingAddrZip || "",
    comment: tester.comment || ""
  };
};

const extractContactInfo = (tester: Tester): Tester => {
  if (tester.testerContacts.length > 0) {
    const contact = tester.testerContacts[0];
    return {
      ...tester,
      pocName: contact.pocName,
      pocPhone: contact.pocPhone,
      pocEmail: contact.pocEmail,
      pocTitle: contact.pocTitle
    };
  }
  return tester;
};

const showErrorModal = (setErrorModal: any, title: string, message: string) => {
  setErrorModal({
    Title: title,
    Show: true,
    Component: <div>{message}</div>,
    HandleClose: () => setErrorModal((prev: ComponentModelPopup) => ({ ...prev, Show: false }))
  });
};

const PhysicalAddressSection = ({ values, errors, touched, handleChange, handleBlur, states }: Omit<FormSectionProps, 'isAuthorized'>) => (
  <Row>
    <Form.Group className={"mb-1 form-floating required " + getFilledClass(values.physicalAddrLine1)} as={Col} sm={12} lg={3}>
      <Form.Control
        autoComplete="off"
        type="text"
        name="physicalAddrLine1"
        maxLength={50}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.physicalAddrLine1}
        className={getErrorClass(touched.physicalAddrLine1, errors.physicalAddrLine1)}
      />
      <Form.Label className="mb-0">Address Line 1</Form.Label>
      {(touched.physicalAddrLine1 && errors.physicalAddrLine1) && <div className="text-danger font-small">{errors.physicalAddrLine1}</div>}
    </Form.Group>
    <Form.Group className={"mb-1 form-floating " + getFilledClass(values.physicalAddrLine2)} as={Col} sm={12} lg={3}>
      <Form.Control
        autoComplete="off"
        type="text"
        name="physicalAddrLine2"
        maxLength={50}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.physicalAddrLine2}
        className={getErrorClass(touched.physicalAddrLine2, errors.physicalAddrLine2)}
      />
      <Form.Label className="mb-0">Address Line 2</Form.Label>
      {(touched.physicalAddrLine2 && errors.physicalAddrLine2) && <div className="text-danger font-small">{errors.physicalAddrLine2}</div>}
    </Form.Group>
    <Form.Group className={"mb-1 form-floating required " + getFilledClass(values.physicalAddrCity)} as={Col}>
      <Form.Control
        autoComplete="off"
        type="text"
        name="physicalAddrCity"
        maxLength={50}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.physicalAddrCity}
        className={getErrorClass(touched.physicalAddrCity, errors.physicalAddrCity)}
      />
      <Form.Label className="mb-0">City</Form.Label>
      {(touched.physicalAddrCity && errors.physicalAddrCity) && <div className="text-danger font-small">{errors.physicalAddrCity}</div>}
    </Form.Group>
    <Form.Group className={"mb-1 form-floating required " + getFilledClass(values.physicalAddrstateCD)} as={Col}>
      <Form.Control
        autoComplete="off"
        as="select"
        name="physicalAddrstateCD"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.physicalAddrstateCD}
        className={getErrorClass(touched.physicalAddrstateCD, errors.physicalAddrstateCD)}
      >
        <option value=""></option>
        {states?.map((state: StateLookUp) => (
          <option key={state.StateCode} value={state.StateCode}>{state.StateCode}</option>
        ))}
      </Form.Control>
      <span style={{ position: 'absolute', right: '10px', pointerEvents: 'none', top: '50%', transform: 'translateY(-50%)' }} className='vehicle-filter-icon'></span>
      <Form.Label className="mb-0">State</Form.Label>
      {(touched.physicalAddrstateCD && errors.physicalAddrstateCD) && <div className="text-danger font-small">{errors.physicalAddrstateCD}</div>}
    </Form.Group>
    <Form.Group className={"mb-1 form-floating required " + getFilledClass(values.physicalAddrZip)} as={Col}>
      <Form.Control
        autoComplete="off"
        type="text"
        name="physicalAddrZip"
        maxLength={50}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.physicalAddrZip}
        className={getErrorClass(touched.physicalAddrZip, errors.physicalAddrZip)}
      />
      <Form.Label className="mb-0">Zip Code</Form.Label>
      {(touched.physicalAddrZip && errors.physicalAddrZip) && <div className="text-danger font-small">{errors.physicalAddrZip}</div>}
    </Form.Group>
  </Row>
);

const MailingAddressSection = ({ values, errors, touched, handleChange, handleBlur, states }: Omit<FormSectionProps, 'isAuthorized'>) => {
  const hasMailingData = hasMailingAddressData(values);

  return (
    <Row>
      <Form.Group className={getMailingFieldClass(values.mailingAddrLine1, hasMailingData)} as={Col} sm={12} lg={3}>
        <Form.Control
          autoComplete="off"
          type="text"
          name="mailingAddrLine1"
          maxLength={50}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.mailingAddrLine1}
          className={getErrorClass(touched.mailingAddrLine1, errors.mailingAddrLine1)}
        />
        <Form.Label className="mb-0">Mailing Address Line 1</Form.Label>
        {(touched.mailingAddrLine1 && errors.mailingAddrLine1) && <div className="text-danger font-small">{errors.mailingAddrLine1}</div>}
      </Form.Group>
      <Form.Group className={"mb-1 form-floating " + getMailingAddressFieldClass(values.mailingAddrLine2)} as={Col} sm={12} lg={3}>
        <Form.Control
          autoComplete="off"
          type="text"
          name="mailingAddrLine2"
          maxLength={50}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.mailingAddrLine2}
          className={getErrorClass(touched.mailingAddrLine2, errors.mailingAddrLine2)}
        />
        <Form.Label className="mb-0">Mailing Address Line 2</Form.Label>
        {(touched.mailingAddrLine2 && errors.mailingAddrLine2) && <div className="text-danger font-small">{errors.mailingAddrLine2}</div>}
      </Form.Group>
      <Form.Group className={getMailingFieldClass(values.mailingAddrCity, hasMailingData)} as={Col}>
        <Form.Control
          autoComplete="off"
          type="text"
          name="mailingAddrCity"
          maxLength={50}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.mailingAddrCity}
          className={getErrorClass(touched.mailingAddrCity, errors.mailingAddrCity)}
        />
        <Form.Label className="mb-0">Mailing City</Form.Label>
        {(touched.mailingAddrCity && errors.mailingAddrCity) && <div className="text-danger font-small">{errors.mailingAddrCity}</div>}
      </Form.Group>
      <Form.Group className={getMailingFieldClass(values.mailingAddrstateCD, hasMailingData)} as={Col}>
        <Form.Control
          autoComplete="off"
          as="select"
          name="mailingAddrstateCD"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.mailingAddrstateCD}
          className={getErrorClass(touched.mailingAddrstateCD, errors.mailingAddrstateCD)}
        >
          <option value=""></option>
          {states?.map((state: StateLookUp) => (
            <option key={state.StateCode} value={state.StateCode}>{state.StateCode}</option>
          ))}
        </Form.Control>
        <span style={{ position: 'absolute', right: '10px', pointerEvents: 'none', top: '50%', transform: 'translateY(-50%)' }} className='vehicle-filter-icon'></span>
        <Form.Label className="mb-0">Mailing State</Form.Label>
        {(touched.mailingAddrstateCD && errors.mailingAddrstateCD) && <div className="text-danger font-small">{errors.mailingAddrstateCD}</div>}
      </Form.Group>
      <Form.Group className={getMailingFieldClass(values.mailingAddrZip, hasMailingData)} as={Col}>
        <Form.Control
          autoComplete="off"
          type="text"
          name="mailingAddrZip"
          maxLength={50}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.mailingAddrZip}
          className={getErrorClass(touched.mailingAddrZip, errors.mailingAddrZip)}
        />
        <Form.Label className="mb-0">Mailing Zip Code</Form.Label>
        {(touched.mailingAddrZip && errors.mailingAddrZip) && <div className="text-danger font-small">{errors.mailingAddrZip}</div>}
      </Form.Group>
    </Row>
  );
};

const ContactInfoSection = ({ values, errors, touched, handleChange, handleBlur }: Omit<FormSectionProps, 'states' | 'isAuthorized'>) => (
  <Row>
    <Form.Group className={getContactFieldClass(values.pocName)} as={Col} sm={12} lg={3}>
      <Form.Control
        autoComplete="off"
        type="text"
        name="pocName"
        maxLength={50}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.pocName}
        className={getErrorClass(touched.pocName, errors.pocName)}
      />
      <Form.Label className="mb-0">Point of Contact Name</Form.Label>
      {(touched.pocName && errors.pocName) && <div className="text-danger font-small">{errors.pocName}</div>}
    </Form.Group>
    <Form.Group className={getContactFieldClass(values.pocTitle)} as={Col} sm={12} lg={3}>
      <Form.Control
        autoComplete="off"
        type="text"
        name="pocTitle"
        maxLength={50}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.pocTitle}
        className={getErrorClass(touched.pocTitle, errors.pocTitle)}
      />
      <Form.Label className="mb-0">Title</Form.Label>
      {(touched.pocTitle && errors.pocTitle) && <div className="text-danger font-small">{errors.pocTitle}</div>}
    </Form.Group>
    <Form.Group className={getContactFieldClass(values.pocPhone)} as={Col} sm={12} lg={3}>
      <Form.Control
        autoComplete="off"
        type="text"
        name="pocPhone"
        maxLength={20}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.pocPhone}
        className={getErrorClass(touched.pocPhone, errors.pocPhone)}
      />
      <Form.Label className="mb-0">Contact Number</Form.Label>
      {(touched.pocPhone && errors.pocPhone) && <div className="text-danger font-small">{errors.pocPhone}</div>}
    </Form.Group>
    <Form.Group className={getContactFieldClass(values.pocEmail)} as={Col} sm={12} lg={3}>
      <Form.Control
        autoComplete="off"
        type="text"
        name="pocEmail"
        maxLength={50}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.pocEmail}
        className={getErrorClass(touched.pocEmail, errors.pocEmail)}
      />
      <Form.Label className="mb-0">Email</Form.Label>
      {(touched.pocEmail && errors.pocEmail) && <div className="text-danger font-small">{errors.pocEmail}</div>}
    </Form.Group>
    <Form.Group className={getContactFieldClass(values.comment, false)} as={Col}>
      <Form.Control
        autoComplete="off"
        as="textarea"
        rows={3}
        name="comment"
        maxLength={4000}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.comment}
        className={getErrorClass(touched.comment, errors.comment)}
      />
      <Form.Label className="mb-0">Comments</Form.Label>
      {(touched.comment && errors.comment) && <div className="text-danger font-small">{errors.comment}</div>}
    </Form.Group>
  </Row>
);

const HeaderSection = ({ values, errors, touched, handleChange, handleBlur, isAuthorized }: Omit<FormSectionProps, 'states'>) => (
  <Row>
    <Form.Group className={"mb-1 form-floating required " + ((values.testerName && values.testerName.length > 0) ? "filled" : "")} as={Col} sm={12} lg={6}>
      <Form.Control
        disabled={!!(values.testerId > 0 && isAuthorized && !isAuthorized("AV_Edit_Tester"))}
        autoComplete="off"
        type="text"
        name="testerName"
        maxLength={50}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.testerName}
        className={touched.testerName && errors.testerName ? "has-error" : ""}
      />
      <Form.Label className="mb-0">Tester</Form.Label>
      {(touched.testerName && errors.testerName) && <div className="text-danger font-small">{errors.testerName}</div>}
    </Form.Group>
    <Form.Group className={"mb-1 form-floating required " + ((values.testerEffectiveDate && (JSON.stringify(values.testerEffectiveDate)).length > 0) ? "filled" : "")} as={Col} sm={12} lg={6}>
      <Form.Control className="form-control-wrapper" as="div">
        <DatePicker onBlur={handleBlur} name="testerEffectiveDate" value={values.testerEffectiveDate} disabled={isAuthorized ? !isAuthorized("AV_Edit_Tester") : false} />
      </Form.Control>
      <Form.Label className="mb-0">Tester Effective Date</Form.Label>
      {touched.testerEffectiveDate && typeof errors.testerEffectiveDate === 'string' && (
        <div className="text-danger font-small">{errors.testerEffectiveDate}</div>
      )}
    </Form.Group>
  </Row>
);

interface FormContentProps extends FormSectionProps {
  isValid: boolean;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
}

const FormContent = ({ values, errors, touched, handleChange, handleBlur, states, isAuthorized, isValid, handleSubmit }: FormContentProps) => (
  <Form onSubmit={handleSubmit}>
    <HeaderSection values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} isAuthorized={isAuthorized} />
    <PhysicalAddressSection values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} states={states} />
    <MailingAddressSection values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} states={states} />
    <ContactInfoSection values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} />
    <div className="row">
      <div className="col-sm-12 text-left mt-5">
        <Button className="shadow-sm me-3 cursor-shadow btn-save btn-default" variant="light" disabled={!(values.testerName && values.testerName.length > 0 && isValid)} type="submit">Save Tester Information</Button>
        <Button className="ml-2 shadow-sm cursor-shadow btn-default btn-cancel" variant="light" onClick={() => { }}>Cancel</Button>
      </div>
    </div>
  </Form>
);

interface FormikRenderProps {
  values: Tester;
  errors: any;
  touched: any;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isValid: boolean;
  states: StateLookUp[];
  isAuthorized: (role: string) => boolean;
  onCancel: () => void;
}

const FormikRender = ({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid, states, isAuthorized, onCancel }: FormikRenderProps) => (
  <Form onSubmit={handleSubmit}>
    <HeaderSection values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} isAuthorized={isAuthorized} />
    <PhysicalAddressSection values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} states={states} />
    <MailingAddressSection values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} states={states} />
    <ContactInfoSection values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} />
    <div className="row">
      <div className="col-sm-12 text-left mt-5">
        <Button className="shadow-sm me-3 cursor-shadow btn-save btn-default" variant="light" disabled={!(values.testerName && values.testerName.length > 0 && isValid)} type="submit">Save Tester Information</Button>
        <Button className="ml-2 shadow-sm cursor-shadow btn-default btn-cancel" variant="light" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  </Form>
);

function EditTester({ testerId, onCancel, onSuccess }: TParams) {

  let { isAuthorized } = useContext(UserAuthContext) as Authorize;

  let [tester, setTester] = useState({ testerId: -1, automatedVehiclesCount: 0, pddsCount: 0, comment: "", isActive: "Y", pocEmail: "", pocTitle: "", mailingAddrCity: "", mailingAddrLine1: "", mailingAddrLine2: "", mailingAddrZip: "", mailingAddrstateCD: "", physicalAddrCity: "", physicalAddrLine1: "", physicalAddrLine2: "", physicalAddrZip: "", physicalAddrstateCD: "", pocName: "", pocPhone: "", testerName: "" } as Tester);

  let [isShowLoading, setIsShowLoading] = useState<boolean>(false);

  let [modalPopup, setModalPopup] = useState({} as ConfirmationModelPopup);

  let [states, setStates] = useState([] as StateLookUp[]);

  let [errorModal, setErrorModal] = useState({
    Title: "Error",
    Show: false,
    Component: null,
    HandleClose: () => setErrorModal({ ...errorModal, Show: false })
  } as ComponentModelPopup);

  let testerService = new TesterService();

  async function GetTesterById(testerId: number) {
    setIsShowLoading(true);

    try {
      const response = await testerService.GetTesterById(testerId);

      if (response.statusCode === "N") {
        const errorMessage = response.statusMessage || "Failed to load tester information";
        console.error("GetTesterById error:", errorMessage);
        showErrorModal(setErrorModal, "Error", errorMessage);
        setIsShowLoading(false);
        return;
      }

      const _tester = response.result;
      if (!_tester) {
        showErrorModal(setErrorModal, "Error", "No tester data returned from server");
        setIsShowLoading(false);
        return;
      }

      const formattedTester = extractContactInfo(formatTesterData(_tester));
      setTester(formattedTester);
      setIsShowLoading(false);
    } catch (error: any) {
      const message = error?.statusMessage || error?.message || "An unexpected error occurred while loading tester";
      console.error("GetTesterById exception:", error);
      showErrorModal(setErrorModal, "Error", message);
      setIsShowLoading(false);
    }
  }

  const GetStateLookUp = () => {
    testerService.GetStateLookUp().then(res => {
      if (res.statusCode === "N") {
        const errorMessage = res.statusMessage || "Failed to load states";
        console.error("GetStateLookUp error:", errorMessage);
        showErrorModal(setErrorModal, "Error", errorMessage);
        return;
      }
      setStates(res.result || []);
    }).catch((error: any) => {
      const message = error?.statusMessage || error?.message || "An unexpected error occurred while loading states";
      console.error("GetStateLookUp exception:", error);
      showErrorModal(setErrorModal, "Error", message);
    });
  }

  useEffect(() => {
    if (testerId === 0) {
      setTester({ testerId: 0, isActive: "Y" } as Tester);
    }
    else {
      GetTesterById(testerId);
    }

    GetStateLookUp();

  }, [testerId]);

  function onModalClose() {
    setModalPopup({} as ConfirmationModelPopup);
  }

  const onSubmitTester = (testerObj: Tester) => {
    setModalPopup({
      Title: "Are you sure you want to save this Tester?",
      BodyCentered: false,
      BodyMessageHtml: `<dl className="row">
            <div className="col-md-6 col-sm-12 p-1">
              <dt>TESTER</dt>
              <dd>` + testerObj.testerName + `</dd>
            </div>  
            <div className="col-md-6 col-sm-12 p-1">
              <dt>TESTER EFFECTIVE DATE</dt>
              <dd>` + getFormattedShortDate(new Date(testerObj.testerEffectiveDate)) + `</dd>
            </div>
            </dl>`,
      OkText: "Yes, Save",
      OkVariant: "btn-save",
      Show: true,
      ShowCancel: true,
      CancelText: "Cancel",
      CancelVariant: "btn-cancel",
      HandleClose: onModalClose,
      HandleOk: () => { OnFormSubmit(testerObj) }
    } as ConfirmationModelPopup);
  };

  const OnFormSubmit = (testerObj: Tester) => {
    setIsShowLoading(true);
    const testerService = new TesterService();

    testerService.SaveTester(testerObj).then(res => {
      if (res.statusCode === "N") {
        const errorMessage = res.statusMessage || "Failed to save tester";
        console.error("SaveTester error:", errorMessage);
        showErrorModal(setErrorModal, "Error", errorMessage);
        setIsShowLoading(false);
        return;
      }
      onModalClose();
      setIsShowLoading(false);
      onSuccess(res.result);
    }).catch((error: any) => {
      const message = error?.statusMessage || error?.message || "An unexpected error occurred while saving tester";
      console.error("SaveTester exception:", error);
      showErrorModal(setErrorModal, "Error", message);
      setIsShowLoading(false);
    });
  };

  return (
    <>
      <ErrorComponentModel modal={errorModal} />
      <div className="row pt-2 pb-2">
        {tester && tester.testerId >= 0 &&
          <div className="col-sm-12">
            <div className="row">
              <div className="col-sm-12">
                <div className="row">
                  <div className="col">
                    <Formik validateOnBlur={true}
                      initialValues={tester}
                      validationSchema={TesterValidationSchema}
                      validate={validateTesterForm}
                      onSubmit={onSubmitTester}
                    >
                      {(formikProps) => {

                        const { values, handleChange, handleBlur, handleSubmit, errors, touched, isValid } = formikProps;



                        return (
                          <Form onSubmit={handleSubmit}>
                            <HeaderSection
                              values={values}
                              errors={errors}
                              touched={touched}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              isAuthorized={isAuthorized}
                            />
                            <PhysicalAddressSection
                              values={values}
                              errors={errors}
                              touched={touched}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              states={states}
                            />
                            <MailingAddressSection
                              values={values}
                              errors={errors}
                              touched={touched}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              states={states}
                            />
                            <ContactInfoSection
                              values={values}
                              errors={errors}
                              touched={touched}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                            <div className="row">
                              <div className="col-sm-12 text-left mt-5">
                                <Button
                                  className="shadow-sm me-3 cursor-shadow btn-save btn-default"
                                  variant="light"
                                  disabled={!(!!values.testerName && values.testerName.length > 0 && !!isValid)}
                                  type="submit"
                                >
                                  Save Tester Information
                                </Button>
                                <Button className="ml-2 shadow-sm cursor-shadow btn-default btn-cancel" variant="light" onClick={onCancel}>Cancel</Button>
                              </div>
                            </div>
                          </Form>
                        )
                      }}
                    </Formik>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        <ConfirmationModel modal={modalPopup}></ConfirmationModel>
      </div>
      {isShowLoading && <Spinner></Spinner>}
    </>
  );
}

export default withAITracking(reactPlugin, EditTester);



